import React, { useState, FormEvent, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch,
  Modal
} from '@mui/material'
import { HiOutlinePlus, HiOutlineTrash, HiOutlineMenu } from 'react-icons/hi'

interface Plan {
  id: number
  title: string
  min_price: number
  ratio: number
  durations: { duration: number }[]
  isActive: boolean
  plan_option_id?: number
}
interface PlanOptionInfo {
  id: number
  title: string
}

interface PlanOptionField {
  id: string
  optionId: string
  type: 'int' | 'text' | 'checkbox' | 'combine' | 'textarea' | 'select' | 'time'
  title: string
  description: string
  note: string
  editable: boolean
  is_required?: boolean
  order?: number
  data?: any[]
  validation?: {
    warning?: [number, number]
    danger?: [number, number]
  }
  [key: string]: any
}

interface PlanFeature {
  title: string
  order?: number
  [key: string]: any
}

type PlanOptions = {
  [planId: number]: {
    [featureId: string]: PlanFeature
  }
}

interface PlansTabProps {
  apiFetch: (url: string, options?: RequestInit) => Promise<any>
  handleApiCall: (apiCall: Promise<any>, successMessage: string) => Promise<any>
  showConfirmationModal: (message: string, onConfirm: () => void) => void
  setError: (error: string | null) => void
  plans: Plan[]
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>
  planOptions: PlanOptions
  setPlanOptions: React.Dispatch<React.SetStateAction<PlanOptions>>
  allPlanOptions: PlanOptionInfo[]
  loading: boolean
}

const PlansTab: React.FC<PlansTabProps> = ({
  apiFetch,
  handleApiCall,
  showConfirmationModal,
  setError,
  plans,
  setPlans,
  planOptions,
  setPlanOptions,
  allPlanOptions,
  loading
}) => {
  const planOptionFormRef = useRef<HTMLFormElement>(null)
  const [selectedPlanOptionId, setSelectedPlanOptionId] = useState<number | ''>('')
  const [editingPlanOptionId, setEditingPlanOptionId] = useState<number | null>(null)
  const [newPlanOptionTitle, setNewPlanOptionTitle] = useState('')
  const [newPlanOptionFields, setNewPlanOptionFields] = useState<PlanOptionField[]>([])

  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false)
  const [editingDescription, setEditingDescription] = useState({ fieldId: '', value: '' })

  const dragField = useRef<number | null>(null)
  const dragOverField = useRef<number | null>(null)
  const [dragging, setDragging] = useState(false)

  const draggedFeature = useRef<{ planId: number | null; index: number | null }>({ planId: null, index: null })
  const dragOverFeature = useRef<{ planId: number | null; index: number | null }>({ planId: null, index: null })
  const [isDraggingFeature, setIsDraggingFeature] = useState(false)

  const [localAllPlanOptions, setLocalAllPlanOptions] = useState<PlanOptionInfo[]>(allPlanOptions)

  const [loadingState, setLoadingState] = useState({
    planSave: null as number | null,
    featureSave: null as number | null,
    planOptionSave: false
  })

  useEffect(() => {
    setLocalAllPlanOptions(allPlanOptions)
  }, [allPlanOptions])

  const handleOpenDescriptionModal = (fieldId: string, currentValue: string) => {
    setEditingDescription({ fieldId, value: currentValue })
    setIsDescriptionModalOpen(true)
  }

  const handleSaveDescription = () => {
    handleOptionFieldChange(editingDescription.fieldId, 'description', editingDescription.value)
    setIsDescriptionModalOpen(false)
    setEditingDescription({ fieldId: '', value: '' })
  }

  const handleCancelEditPlanOption = () => {
    setEditingPlanOptionId(null)
    setNewPlanOptionTitle('')
    setNewPlanOptionFields([])
    setError(null)
    setSelectedPlanOptionId('')
  }

  const handleEditPlanOption = async (optionId: number) => {
    setEditingPlanOptionId(optionId)
    setNewPlanOptionTitle('')
    setNewPlanOptionFields([])
    setError(null)

    if (planOptionFormRef.current) {
      planOptionFormRef.current.scrollIntoView({ behavior: 'smooth' })
    }

    try {
      const response = await apiFetch(`/plan/options/${optionId}`)
      setNewPlanOptionTitle(response.title || '')

      const mappedFields = Object.entries(response.data || {}).map(([key, value]: [string, any]) => {
        const baseObject = (value || {}) as Partial<PlanOptionField>
        return {
          ...baseObject,
          optionId: key,
          id: `field_${key}_${Date.now()}`
        }
      })

      const sortedFields = mappedFields.sort((a, b) => (a.order || 1) - (b.order || 1))

      setNewPlanOptionFields(sortedFields as PlanOptionField[])
    } catch (err: any) {
      setError(`خطا در بارگذاری آپشن پلن: ${err.message}`)
      handleCancelEditPlanOption()
    }
  }

  const handleDeletePlanOption = (optionId: number) => {
    showConfirmationModal(`آیا از حذف آپشن پلن با ID ${optionId} مطمئن هستید؟`, () => {
      handleApiCall(
        apiFetch(`/plan/options/${optionId}`, { method: 'DELETE' }),
        `آپشن پلن با ID ${optionId} با موفقیت حذف شد.`
      ).then(() => {
        setLocalAllPlanOptions(prev => prev.filter(opt => opt.id !== optionId))
        handleCancelEditPlanOption()
      })
    })
  }

  const handleAddNewOptionField = (type: PlanOptionField['type']) => {
    const newField: PlanOptionField = {
      id: `field_${Date.now()}`,
      optionId: '',
      type,
      title: '',
      description: '',
      note: '',
      editable: true,
      is_required: true
    }
    if (type === 'combine') {
      newField.data = []
      delete newField.is_required
    }
    setNewPlanOptionFields(prev => [...prev, newField])
  }

  const handleRemoveOptionField = (id: string) => {
    setNewPlanOptionFields(prev => prev.filter(field => field.id !== id))
  }

  const handleOptionFieldChange = (id: string, fieldName: string, value: any) => {
    setNewPlanOptionFields(prevFields =>
      prevFields.map(field => (field.id === id ? { ...field, [fieldName]: value } : field))
    )
  }

  const handleSortFields = () => {
    if (dragField.current === null || dragOverField.current === null) return
    const fieldsClone = JSON.parse(JSON.stringify(newPlanOptionFields))
    const draggedItemContent = fieldsClone.splice(dragField.current, 1)[0]
    fieldsClone.splice(dragOverField.current, 0, draggedItemContent)

    dragField.current = null
    dragOverField.current = null
    setDragging(false)
    setNewPlanOptionFields(fieldsClone)
  }

  const handleSortPlanFeatures = (planId: number) => {
    if (
      draggedFeature.current.planId !== planId ||
      dragOverFeature.current.planId !== planId ||
      draggedFeature.current.index === null ||
      dragOverFeature.current.index === null
    ) {
      setIsDraggingFeature(false)
      return
    }

    const featuresObject = planOptions[planId]
    const featuresArray = Object.entries(featuresObject).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))

    const draggedItemContent = featuresArray.splice(draggedFeature.current.index, 1)[0]
    featuresArray.splice(dragOverFeature.current.index, 0, draggedItemContent)

    const newFeaturesObject = featuresArray.reduce((acc, [key, value], index) => {
      acc[key] = { ...value, order: index + 1 }
      return acc
    }, {} as { [key: string]: PlanFeature })

    setPlanOptions(prev => ({
      ...prev,
      [planId]: newFeaturesObject
    }))

    draggedFeature.current = { planId: null, index: null }
    dragOverFeature.current = { planId: null, index: null }
    setIsDraggingFeature(false)
  }

  const handleAddCombineItem = (
    fieldId: string,
    itemType: 'string' | 'text' | 'int' | 'select' | 'time' | 'textarea'
  ) => {
    let newItem: any
    if (itemType === 'string') {
      newItem = 'متن ثابت جدید'
    } else {
      newItem = { type: itemType, id: '', required: true }
      if (itemType === 'select') {
        newItem.options = []
      }
    }

    setNewPlanOptionFields(prevFields =>
      prevFields.map(field => {
        if (field.id === fieldId && field.type === 'combine') {
          const updatedData = [...(field.data || []), newItem]
          return { ...field, data: updatedData }
        }
        return field
      })
    )
  }

  const handleRemoveCombineItem = (fieldId: string, itemIndex: number) => {
    setNewPlanOptionFields(prevFields =>
      prevFields.map(field => {
        if (field.id === fieldId && field.type === 'combine') {
          const updatedData = (field.data || []).filter((_, index) => index !== itemIndex)
          return { ...field, data: updatedData }
        }
        return field
      })
    )
  }

  const handleCombineItemChange = (fieldId: string, itemIndex: number, propName: string | null, value: any) => {
    setNewPlanOptionFields(prevFields =>
      prevFields.map(field => {
        if (field.id === fieldId && field.type === 'combine') {
          const updatedData = (field.data || []).map((item, index) => {
            if (index === itemIndex) {
              if (propName === null) {
                return value
              }
              return { ...item, [propName]: value }
            }
            return item
          })
          return { ...field, data: updatedData }
        }
        return field
      })
    )
  }

  const renderOptionFieldInputs = (field: PlanOptionField) => {
    const commonProps = (name: string) => ({
      name,
      value: field[name] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleOptionFieldChange(field.id, name, e.target.value),
      variant: 'outlined' as 'outlined',
      size: 'small' as 'small',
      fullWidth: true,
      sx: { mb: 1 }
    })

    const validationProps = (name: string, index: number) => {
      let value = ''

      if (field.validation && field.validation[name] && field.validation[name][index] != null) {
        value = field.validation[name][index]
      }

      let onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value === '' ? null : Number(e.target.value)

        setNewPlanOptionFields(prevFields =>
          prevFields.map(f => {
            if (f.id === field.id) {
              if (f.validation == null) {
                f.validation = {
                  warning: [null, null],
                  danger: [null, null]
                }
              }

              if (f.validation[name] == null || !Array.isArray(f.validation[name])) {
                f.validation[name] = [null, null]
              }

              f.validation[name][index] = newValue
            }
            return f
          })
        )
      }

      return {
        name,
        value: value,
        onChange: onChange,
        variant: 'outlined' as 'outlined',
        size: 'small' as 'small',
        fullWidth: true,
        sx: { mb: 1 }
      }
    }

    switch (field.type) {
      case 'int':
        return (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField label='حداقل (Minimum)' type='number' {...commonProps('minimum')} />
            </Grid>
            <Grid item xs={6}>
              <TextField label='حداکثر (Maximum)' type='number' {...commonProps('maximum')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label='مقدار پیشفرض (Default)' type='number' {...commonProps('default')} />
            </Grid>
            <Grid item xs={3}>
              <TextField label='آستانه هشدار از' type='number' {...validationProps('warning', 0)} />
            </Grid>
            <Grid item xs={3}>
              <TextField label='آستانه هشدار تا' type='number' {...validationProps('warning', 1)} />
            </Grid>
            <Grid item xs={3}>
              <TextField label='آستانه خطر از' type='number' {...validationProps('danger', 0)} />
            </Grid>
            <Grid item xs={3}>
              <TextField label='آستانه خطر تا' type='number' {...validationProps('danger', 1)} />
            </Grid>
          </Grid>
        )
      case 'text':
      case 'textarea':
        return (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField label='حداقل طول' type='number' {...commonProps('min_length')} />
            </Grid>
            <Grid item xs={6}>
              <TextField label='حداکثر طول' type='number' {...commonProps('max_length')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label='متن جایگزین (Placeholder)' {...commonProps('place_holder')} />
            </Grid>
          </Grid>
        )
      case 'select':
        return (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField label='گزینه‌ها (با کاما جدا کنید)' {...commonProps('options')} helperText='مثال: man,woman' />
            </Grid>
          </Grid>
        )
      case 'time':
        return (
          <Typography variant='body2' color='text.secondary'>
            تنظیمات خاصی برای این نوع وجود ندارد.
          </Typography>
        )
      case 'checkbox':
        return (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                label='گزینه‌ها (با کاما جدا کنید)'
                {...commonProps('options')}
                helperText='مثال: item1,item2,item3'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='پیشفرض‌ها (با کاما جدا کنید)'
                {...commonProps('defaults')}
                helperText='مثال: item1,item3'
              />
            </Grid>
          </Grid>
        )
      case 'combine':
        return (
          <Box sx={{ mt: 1, p: 2, border: `1px solid var(--border-color)`, borderRadius: '8px' }}>
            <Typography variant='subtitle2' sx={{ mb: 2 }}>
              آیتم‌های فیلد ترکیبی
            </Typography>
            {(field.data || []).map((item, index) => (
              <Paper
                key={index}
                variant='outlined'
                sx={{ p: 2, mb: 2, position: 'relative', background: 'transparent' }}
              >
                <IconButton
                  size='small'
                  sx={{ position: 'absolute', top: 4, left: 4 }}
                  onClick={() => handleRemoveCombineItem(field.id, index)}
                >
                  <HiOutlineTrash color='red' />
                </IconButton>
                {typeof item === 'string' ? (
                  <TextField
                    label='متن ثابت'
                    value={item}
                    onChange={e => handleCombineItemChange(field.id, index, null, e.target.value)}
                    fullWidth
                    variant='standard'
                  />
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant='caption' display='block'>
                        نوع آیتم: <strong>{item.type}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label='شناسه (ID)'
                        required
                        value={item.id || ''}
                        onChange={e => handleCombineItemChange(field.id, index, 'id', e.target.value)}
                        fullWidth
                        size='small'
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.required !== false}
                            onChange={e => handleCombineItemChange(field.id, index, 'required', e.target.checked)}
                          />
                        }
                        label='پر کردن این آیتم اجباری است'
                      />
                    </Grid>
                    {item.type === 'select' && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label='گزینه‌ها (با کاما جدا کنید)'
                          value={(item.options || []).join(',')}
                          onChange={e => handleCombineItemChange(field.id, index, 'options', e.target.value.split(','))}
                          fullWidth
                          size='small'
                          helperText='مثال: man,woman'
                        />
                      </Grid>
                    )}
                    {['text', 'textarea'].includes(item.type) && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            type='number'
                            label='حداقل طول'
                            value={item.min_length || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'min_length', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            type='number'
                            label='حداکثر طول'
                            value={item.max_length || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'max_length', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label='متن جایگزین'
                            value={item.place_holder || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'place_holder', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label='مقدار پیشفرض'
                            value={item.default || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'default', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                      </>
                    )}
                    {item.type === 'int' && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            type='number'
                            label='حداقل'
                            value={item.minimum || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'minimum', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            type='number'
                            label='حداکثر'
                            value={item.maximum || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'maximum', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type='number'
                            label='مقدار پیشفرض'
                            value={item.default || ''}
                            onChange={e => handleCombineItemChange(field.id, index, 'default', e.target.value)}
                            fullWidth
                            size='small'
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </Paper>
            ))}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'string')}>
                + افزودن متن ثابت
              </Button>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'text')}>
                + ورودی متن
              </Button>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'textarea')}>
                + ورودی متن چندخطی
              </Button>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'int')}>
                + ورودی عدد
              </Button>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'select')}>
                + لیست انتخاب
              </Button>
              <Button size='small' variant='text' onClick={() => handleAddCombineItem(field.id, 'time')}>
                + ورودی زمان
              </Button>
            </Box>
          </Box>
        )
      default:
        return null
    }
  }

  const handleSavePlanOption = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    if (!newPlanOptionTitle) {
      setError('عنوان آپشن پلن الزامی است.')
      return
    }

    const data: { [key: string]: any } = {}
    for (const [index, field] of newPlanOptionFields.entries()) {
      if (!field.optionId) {
        setError(`شناسه آپشن (Option ID) برای فیلد "${field.title || field.id}" الزامی است.`)
        return
      }
      if (data[field.optionId]) {
        setError(`شناسه آپشن (Option ID) "${field.optionId}" تکراری است.`)
        return
      }

      const { id, optionId, ...rest } = field

      if (rest.type === 'int') {
        if (rest.minimum != null && rest.minimum !== '') rest.minimum = Number(rest.minimum)
        if (rest.maximum != null && rest.maximum !== '') rest.maximum = Number(rest.maximum)
        if (rest.default != null && rest.default !== '') rest.default = Number(rest.default)
      }

      if (rest.type === 'select' || rest.type === 'checkbox') {
        if (rest.options && typeof rest.options === 'string') {
          rest.options = rest.options
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        }
        if (rest.defaults && typeof rest.defaults === 'string') {
          rest.defaults = rest.defaults
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        }
      }

      if (rest.type === 'combine') {
        const sanitizedData = []
        for (const item of rest.data || []) {
          if (typeof item === 'object' && !item.id) {
            setError(`یکی از آیتم‌ها در فیلد ترکیبی "${field.title}" شناسه (ID) ندارد.`)
            return
          }
          if (typeof item === 'string') {
            sanitizedData.push(item)
          } else {
            const { id: itemId, type: itemType } = item
            const sanitizedItem: any = { type: itemType, id: itemId }
            sanitizedItem.required = item.required !== false

            if (itemType === 'select' && item.options) {
              sanitizedItem.options = Array.isArray(item.options)
                ? item.options.map(String)
                : String(item.options)
                    .split(',')
                    .map((s: string) => s.trim())
                    .filter(Boolean)
            }
            if (['text', 'textarea'].includes(itemType)) {
              if (item.default) sanitizedItem.default = item.default
              if (item.min_length) sanitizedItem.min_length = Number(item.min_length)
              if (item.max_length) sanitizedItem.max_length = Number(item.max_length)
              if (item.place_holder) sanitizedItem.place_holder = item.place_holder
            }
            if (itemType === 'int') {
              if (item.default != null && item.default !== '') sanitizedItem.default = Number(item.default)
              if (item.minimum != null && item.minimum !== '') sanitizedItem.minimum = Number(item.minimum)
              if (item.maximum != null && item.maximum !== '') sanitizedItem.maximum = Number(item.maximum)
            }
            sanitizedData.push(sanitizedItem)
          }
        }
        rest.data = sanitizedData
      }

      const finalFieldData = { ...rest, order: index + 1 }
      if (rest.type === 'combine') {
        delete finalFieldData.is_required
      } else {
        finalFieldData.is_required = rest.is_required !== false
      }
      data[field.optionId] = finalFieldData
    }

    const payload = { title: newPlanOptionTitle, data }
    setLoadingState(prev => ({ ...prev, planOptionSave: true }))

    const method = editingPlanOptionId ? 'PUT' : 'POST'
    const url = editingPlanOptionId ? `/plan/options/${editingPlanOptionId}` : '/plan/options'
    const successMessage = editingPlanOptionId
      ? `آپشن پلن "${newPlanOptionTitle}" با موفقیت ویرایش شد.`
      : `آپشن پلن جدید "${newPlanOptionTitle}" با موفقیت ساخته شد.`

    try {
      await handleApiCall(apiFetch(url, { method, body: JSON.stringify(payload) }), successMessage)
      handleCancelEditPlanOption()
    } catch (err) {
    } finally {
      setLoadingState(prev => ({ ...prev, planOptionSave: false }))
    }
  }

  const handleFeatureTitleChange = (planId: number, featureId: string, newTitle: string) => {
    setPlanOptions(prevOptions => ({
      ...prevOptions,
      [planId]: {
        ...prevOptions[planId],
        [featureId]: {
          ...prevOptions[planId][featureId],
          title: newTitle
        }
      }
    }))
  }

  const handleSaveFeatures = async (plan: Plan) => {
    if (!plan.plan_option_id) {
      setError('این پلن شناسه‌ی آپشن برای ذخیره‌سازی ندارد.')
      return
    }
    setLoadingState(prev => ({ ...prev, featureSave: plan.id }))

    const featuresObject = planOptions[plan.id]
    const featuresArray = Object.entries(featuresObject).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
    const dataWithOrder = featuresArray.reduce((acc, [key, value], index) => {
      acc[key] = { ...value, order: index + 1 }
      return acc
    }, {} as { [key: string]: PlanFeature })

    const payload = {
      title: allPlanOptions.find(opt => opt.id === plan.plan_option_id)?.title || 'Unknown Title',
      data: dataWithOrder
    }

    await handleApiCall(
      apiFetch(`/plan/options/${plan.plan_option_id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      }),
      'ویژگی‌های پلن با موفقیت به‌روز شد.'
    )

    setLoadingState(prev => ({ ...prev, featureSave: null }))
  }

  return (
    <>
      <Modal open={isDescriptionModalOpen} onClose={() => setIsDescriptionModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600 },
            bgcolor: 'var(--background-color-secondary)',
            color: 'var(--text-color)',
            border: '1px solid var(--border-color)',
            boxShadow: 24,
            p: 4,
            borderRadius: '12px'
          }}
        >
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            ویرایش توضیحات
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={editingDescription.value}
            onChange={e => setEditingDescription(prev => ({ ...prev, value: e.target.value }))}
            variant='outlined'
            sx={{
              textarea: { color: 'var(--text-color)' },
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-color)' }
            }}
          />
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant='outlined' onClick={() => setIsDescriptionModalOpen(false)}>
              لغو
            </Button>
            <Button variant='contained' color='primary' onClick={handleSaveDescription}>
              ذخیره
            </Button>
          </Box>
        </Box>
      </Modal>

      <Typography variant='h5' sx={{ mb: 3 }}>
        مدیریت آپشن‌های پلن
      </Typography>

      <Paper className='management-card'>
        <div className='simple-input-group'>
          <label>یک آپشن برای ویرایش یا حذف انتخاب کنید</label>
          <Select
            value={selectedPlanOptionId}
            onChange={e => {
              const id = e.target.value as number
              if (id) {
                setSelectedPlanOptionId(id)
                handleEditPlanOption(id)
              }
            }}
            displayEmpty
          >
            <MenuItem value='' disabled>
              <em>یک مورد را انتخاب کنید...</em>
            </MenuItem>
            {localAllPlanOptions.map(opt => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.title} (ID: {opt.id})
              </MenuItem>
            ))}
          </Select>
        </div>
        {selectedPlanOptionId && (
          <Button
            color='error'
            variant='text'
            onClick={() => handleDeletePlanOption(selectedPlanOptionId as number)}
            startIcon={<HiOutlineTrash />}
          >
            حذف این آپشن
          </Button>
        )}
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Paper component='form' className='management-card' onSubmit={handleSavePlanOption} ref={planOptionFormRef}>
        <h3 className='tab-section-header'>
          {editingPlanOptionId ? `ویرایش آپشن پلن (ID: ${editingPlanOptionId})` : 'ساخت آپشن پلن جدید'}
        </h3>
        <div className='simple-input-group'>
          <label>عنوان آپشن پلن</label>
          <input
            type='text'
            value={newPlanOptionTitle}
            onChange={e => setNewPlanOptionTitle(e.target.value)}
            required
          />
        </div>
        <Divider sx={{ my: 2 }} />
        <Typography variant='h6' sx={{ mb: 2 }}>
          ویژگی‌های آپشن (Fields)
        </Typography>

        <Box>
          {newPlanOptionFields.map((field, index) => {
            const commonProps = (name: string) => ({
              name,
              value: field[name] || '',
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                handleOptionFieldChange(field.id, name, e.target.value),
              variant: 'outlined' as 'outlined',
              size: 'small' as 'small',
              fullWidth: true,
              sx: { mb: 1 }
            })
            const commonCheckbox = (name: string) => ({
              checked: field[name] === undefined ? true : field[name],
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                handleOptionFieldChange(field.id, name, e.target.checked)
            })
            return (
              <Card
                key={field.id}
                className='plan-option-field-card'
                sx={{
                  transform: dragging && dragField.current === index ? 'scale(1.02)' : 'scale(1)',
                  opacity: dragging && dragField.current === index ? 0.8 : 1
                }}
                draggable
                onDragStart={() => {
                  dragField.current = index
                  setDragging(true)
                }}
                onDragEnter={() => (dragOverField.current = index)}
                onDragEnd={handleSortFields}
                onDragOver={e => e.preventDefault()}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                      <HiOutlineMenu style={{ marginRight: '8px' }} />
                      <Typography variant='subtitle1' component='div'>
                        فیلد از نوع: <strong>{field.type}</strong>
                      </Typography>
                    </Box>
                    <IconButton color='error' size='small' onClick={() => handleRemoveOptionField(field.id)}>
                      <HiOutlineTrash />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label='شناسه آپشن (Option ID)'
                        helperText='انگلیسی و بدون فاصله. مثال: max_loss'
                        {...commonProps('optionId')}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label='عنوان نمایشی (Title)' {...commonProps('title')} required />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label='توضیحات (Description)'
                        value={field.description || ''}
                        onClick={() => handleOpenDescriptionModal(field.id, field.description || '')}
                        InputProps={{ readOnly: true }}
                        variant='outlined'
                        size='small'
                        fullWidth
                        helperText='برای ویرایش کلیک کنید'
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      {renderOptionFieldInputs(field)}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label='یادداشت (Note)' {...commonProps('note')} multiline rows={2} />
                      <FormControlLabel
                        sx={{ mt: 1, color: 'text.secondary' }}
                        control={<Checkbox {...commonCheckbox('editable')} />}
                        label='قابل ویرایش توسط کاربر'
                      />
                      {field.type !== 'combine' && (
                        <FormControlLabel
                          sx={{ mt: 1, color: 'text.secondary' }}
                          control={<Checkbox {...commonCheckbox('is_required')} />}
                          label='پر کردن این فیلد اجباری است'
                        />
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
          <Button
            size='small'
            variant='outlined'
            startIcon={<HiOutlinePlus />}
            onClick={() => handleAddNewOptionField('int')}
          >
            افزودن فیلد عددی
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={<HiOutlinePlus />}
            onClick={() => handleAddNewOptionField('text')}
          >
            افزودن فیلد متنی
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={<HiOutlinePlus />}
            onClick={() => handleAddNewOptionField('checkbox')}
          >
            افزودن فیلد چک‌باکس
          </Button>
          <Button
            size='small'
            variant='outlined'
            startIcon={<HiOutlinePlus />}
            onClick={() => handleAddNewOptionField('combine')}
          >
            افزودن فیلد ترکیبی
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button type='submit' variant='contained' color='success' disabled={loadingState.planOptionSave}>
            {loadingState.planOptionSave ? (
              <CircularProgress size={24} />
            ) : editingPlanOptionId ? (
              'ذخیره تغییرات'
            ) : (
              'ذخیره آپشن پلن'
            )}
          </Button>
          {editingPlanOptionId && (
            <Button variant='outlined' color='secondary' onClick={handleCancelEditPlanOption}>
              لغو ویرایش
            </Button>
          )}
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Paper
        component='form'
        className='management-card'
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault()
          const form = e.currentTarget
          const formData = new FormData(form)
          const durationValues = [
            formData.get('duration1'),
            formData.get('duration2'),
            formData.get('duration3'),
            formData.get('duration4')
          ]
            .filter(d => d)
            .map(d => ({ duration: Number(d) }))
          const newPlan = {
            title: formData.get('title') as string,
            description: formData.get('description') as string,
            isActive: formData.get('isActive') === 'on',
            is_one_time: formData.get('is_one_time') === 'on',
            plan_option_id: Number(formData.get('plan_option_id')),
            min_price: Number(formData.get('min_price')),
            ratio: Number(formData.get('ratio')),
            durations: durationValues
          }
          handleApiCall(
            apiFetch(`/plan/`, { method: 'POST', body: JSON.stringify(newPlan) }),
            'پلن جدید با موفقیت ساخته شد.'
          ).then(() => form.reset())
        }}
      >
        <h3 className='tab-section-header'>افزودن پلن جدید</h3>
        <div className='simple-input-group'>
          <label>نام پلن</label>
          <input type='text' name='title' required />
        </div>
        <div className='simple-input-group'>
          <label>قیمت پایه (حداقل قیمت)</label>
          <input type='number' name='min_price' required />
        </div>
        <div className='simple-input-group'>
          <label>نسبت (Ratio)</label>
          <input type='number' name='ratio' step='0.001' required />
        </div>
        <div className='simple-input-group'>
          <label>مدت‌ها (4 مقدار برای روز)</label>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <input type='number' name='duration1' placeholder='روز اول' />
            </Grid>
            <Grid item xs={6} sm={3}>
              <input type='number' name='duration2' placeholder='روز دوم' />
            </Grid>
            <Grid item xs={6} sm={3}>
              <input type='number' name='duration3' placeholder='روز سوم' />
            </Grid>
            <Grid item xs={6} sm={3}>
              <input type='number' name='duration4' placeholder='روز چهارم' />
            </Grid>
          </Grid>
        </div>
        <div className='simple-input-group'>
          <label>شناسه آپشن‌ها (Plan Option)</label>
          <Select name='plan_option_id' defaultValue='' required>
            <MenuItem value='' disabled>
              <em>یک آپشن انتخاب کنید...</em>
            </MenuItem>
            {allPlanOptions.map(opt => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.id} - {opt.title}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className='simple-input-group'>
          <label>پلن یک‌بار خرید</label>
          <Switch name='is_one_time' defaultChecked />
        </div>
        <Button type='submit' variant='contained'>
          افزودن پلن
        </Button>
      </Paper>

      <Typography variant='h5' sx={{ mb: 3, mt: 4 }}>
        مدیریت پلن‌های اشتراک
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : plans.length > 0 ? (
        plans.map(plan => (
          <Paper
            key={plan.id}
            component='form'
            className='management-card'
            onSubmit={e => {
              e.preventDefault()
              setLoadingState(prev => ({ ...prev, planSave: plan.id }))
              const formData = new FormData(e.currentTarget)
              const durationValues = [
                formData.get('duration1'),
                formData.get('duration2'),
                formData.get('duration3'),
                formData.get('duration4')
              ]
                .filter(d => d)
                .map(d => ({ duration: Number(d) }))
              const updatedPlan = {
                title: formData.get('title') as string,
                min_price: Number(formData.get('min_price')),
                ratio: Number(formData.get('ratio')),
                isActive: formData.get('isActive') === 'on',
                durations: durationValues
              }
              handleApiCall(
                apiFetch(`/plan/${plan.id}`, { method: 'PATCH', body: JSON.stringify(updatedPlan) }),
                'پلن با موفقیت ویرایش شد.'
              ).finally(() => setLoadingState(prev => ({ ...prev, planSave: null })))
            }}
          >
            <div className='simple-input-group'>
              <label>
                نام پلن (ID: {plan.id}) | Option ID: {plan.plan_option_id || 'N/A'}
              </label>
              <input type='text' name='title' defaultValue={plan.title || ''} required />
            </div>
            <div className='simple-input-group'>
              <label>قیمت پایه (حداقل قیمت)</label>
              <input type='number' name='min_price' defaultValue={plan.min_price || 0} required />
            </div>
            <div className='simple-input-group'>
              <label>نسبت (Ratio)</label>
              <input type='number' name='ratio' defaultValue={plan.ratio || 0} step='0.1' required />
            </div>
            <div className='simple-input-group'>
              <label>مدت‌ دوره</label>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <input
                    type='number'
                    name='duration1'
                    placeholder='مدت اول'
                    defaultValue={plan.durations[0]?.duration || ''}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <input
                    type='number'
                    name='duration2'
                    placeholder='مدت دوم'
                    defaultValue={plan.durations[1]?.duration || ''}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <input
                    type='number'
                    name='duration3'
                    placeholder='مدت سوم'
                    defaultValue={plan.durations[2]?.duration || ''}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <input
                    type='number'
                    name='duration4'
                    placeholder='مدت چهارم'
                    defaultValue={plan.durations[3]?.duration || ''}
                  />
                </Grid>
              </Grid>
            </div>
            <div className='simple-input-group'>
              <label>ویژگی‌های پلن</label>
              <Box sx={{ p: 2, border: `1px solid var(--border-color)`, borderRadius: '8px' }}>
                {planOptions[plan.id] && Object.keys(planOptions[plan.id]).length > 0 ? (
                  Object.entries(planOptions[plan.id])
                    .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
                    .map(([featureId, featureData], index) => (
                      <Box
                        key={featureId}
                        className='simple-input-group small'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1,
                          borderRadius: '4px',
                          backgroundColor:
                            isDraggingFeature &&
                            draggedFeature.current?.index === index &&
                            draggedFeature.current?.planId === plan.id
                              ? 'action.focus'
                              : 'transparent'
                        }}
                        draggable
                        onDragStart={() => {
                          draggedFeature.current = { planId: plan.id, index: index }
                          setIsDraggingFeature(true)
                        }}
                        onDragEnter={() => {
                          dragOverFeature.current = { planId: plan.id, index: index }
                        }}
                        onDragEnd={() => handleSortPlanFeatures(plan.id)}
                        onDragOver={e => e.preventDefault()}
                      >
                        <HiOutlineMenu style={{ marginRight: '8px', cursor: 'grab' }} />
                        <input
                          type='text'
                          value={featureData.title || ''}
                          onChange={e => handleFeatureTitleChange(plan.id, featureId, e.target.value)}
                        />
                      </Box>
                    ))
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    هیچ آپشنی برای این پلن ثبت نشده است.
                  </Typography>
                )}
                {plan.plan_option_id && Object.keys(planOptions[plan.id] || {}).length > 0 && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    sx={{ mt: 2 }}
                    disabled={loadingState.featureSave === plan.id}
                    onClick={() => handleSaveFeatures(plan)}
                  >
                    {loadingState.featureSave === plan.id ? <CircularProgress size={20} /> : 'ذخیره ویژگی‌ها'}
                  </Button>
                )}
              </Box>
            </div>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Button type='submit' variant='contained' disabled={loadingState.planSave === plan.id}>
                {loadingState.planSave === plan.id ? <CircularProgress size={24} /> : 'ذخیره کلیات پلن'}
              </Button>
              <Button
                variant='outlined'
                color='primary'
                disabled={!plan.plan_option_id}
                onClick={() => {
                  if (plan.plan_option_id) {
                    setSelectedPlanOptionId(plan.plan_option_id)
                    handleEditPlanOption(plan.plan_option_id)
                  }
                }}
              >
                ویرایش آپشن
              </Button>
              <Button
                variant='outlined'
                color='error'
                onClick={() => {
                  showConfirmationModal('آیا از حذف این پلن مطمئن هستید؟', () =>
                    handleApiCall(apiFetch(`/plan/${plan.id}`, { method: 'DELETE' }), 'پلن با موفقیت حذف شد.')
                  )
                }}
              >
                حذف پلن
              </Button>
            </Box>
          </Paper>
        ))
      ) : (
        <Typography>هیچ پلنی یافت نشد. یک پلن جدید اضافه کنید.</Typography>
      )}
    </>
  )
}

export default PlansTab
